/**
 * Página de Producto con ReelsToBuy
 * Ruta: /reel/:slug
 *
 * Carga producto desde el fan site (VITE_API_URL) y lo muestra con ReelsToBuy.
 * Detecta país automáticamente y redirige a la pasarela correcta.
 */

import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '@/config/apiUrl';
import { useParams, useNavigate } from 'react-router-dom';
import ReelsToBuy, { MediaItem } from '@/components/ReelsToBuy';
import { getDatafastAttributionPayload } from "@/lib/datafast";

interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_usd: number;
  price_ars?: number;
  recommended_price: number;
  recommended_currency: string;
  image_url?: string;
  image_alt?: string;
  hero_image_url?: string;
  thumbnail_url?: string;
  gallery_images?: string[];
  video_url?: string; // Mux Playback ID
  cta_button_text?: string;
  cta_button_color?: string;
  show_quick_buy?: boolean;
  page_title?: string;
  page_description?: string;
  metadata?: Record<string, unknown>;
  stripe_price_id?: string;
  mercadopago_plan_id?: string;
}

// Helper para detectar país del usuario
async function detectUserCountry(): Promise<string> {
  try {
    // Opción 1: Cloudflare headers (si está disponible)
    const cfCountry = (window as unknown as { __CF_COUNTRY__?: string }).__CF_COUNTRY__;
    if (cfCountry) return cfCountry;
    
    // Opción 2: ipapi.co
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return data.country_code || 'US';
  } catch (error) {
    console.error('Error detecting country:', error);
    return 'US'; // Default
  }
}



export default function ProductReel() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProduct() {
      if (!slug) {
        setError('Slug no proporcionado');
        setLoading(false);
        return;
      }

      try {
        // 1. Detectar país del usuario
        const country = await detectUserCountry();
        
        // 2. Obtener producto de la API (fan site via VITE_API_URL)
        const apiBase = getApiBaseUrl();
        const response = await fetch(
          `${apiBase}/api/products/by-slug?slug=${slug}&country=${country}`
        );
        
        if (!response.ok) {
          throw new Error('Producto no encontrado');
        }
        
        const { product } = await response.json();
        setProduct(product);
        
        // 3. Crear items para ReelsToBuy
        const reelItems: MediaItem[] = [];
        
        // Prioridad 1: Video de Mux (si existe)
        if (product.video_url) {
          reelItems.push({
            type: "video",
            src: product.video_url, // Mux Playback ID
            thumbnail: product.thumbnail_url || product.image_url
          });
        }
        
        // Prioridad 2: Imagen principal
        if (product.image_url) {
          reelItems.push({
            type: "image",
            src: product.image_url,
            alt: product.image_alt || product.name
          });
        }
        
        // Agregar galería si existe
        if (product.gallery_images && Array.isArray(product.gallery_images)) {
          product.gallery_images.forEach((imageUrl: string) => {
            reelItems.push({
              type: "image",
              src: imageUrl,
              alt: product.image_alt || product.name
            });
          });
        }
        
        // Si no hay nada, usar placeholder
        if (reelItems.length === 0) {
          reelItems.push({
            type: "image",
            src: "https://via.placeholder.com/400x600?text=" + encodeURIComponent(product.name),
            alt: product.name
          });
        }
        
        setItems(reelItems);
        
        // 4. Actualizar meta tags para SEO
        if (product.page_title) {
          document.title = product.page_title;
        }
        
        if (product.page_description) {
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', product.page_description);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading product:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar producto');
        setLoading(false);
      }
    }
    
    loadProduct();
  }, [slug]);

  // Función de compra
  const handlePurchase = async () => {
    if (!product) return;

    try {
      // 1. Detectar país (solo geo/analytics — pago siempre Stripe)
      const country = await detectUserCountry();

      if (import.meta.env.DEV) {
        console.log(`País detectado: ${country}, Pasarela: stripe`);
      }

      // Stripe-only (MercadoPago deshabilitado). Checkout en USD.
      const response = await fetch('/api/stripe/create-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...getDatafastAttributionPayload(),
          priceId: product.stripe_price_id,
          productId: product.id,
          slug: product.slug,
          productName: product.name,
          price: product.price_usd ?? product.recommended_price,
          currency: 'usd',
          country,
        }),
      });

      if (!response.ok) {
        throw new Error('Error al crear sesión de Stripe');
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      } else {
        navigate(`/buy/${slug}`);
      }
    } catch (error) {
      console.error('Error during purchase:', error);
      alert('Error al procesar la compra. Por favor intenta de nuevo.');
    }
  };

  // Estados de carga y error
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center text-white px-4">
          <h1 className="text-2xl font-bold mb-4">Producto no encontrado</h1>
          <p className="mb-6">{error || 'El producto que buscas no existe'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-white text-black rounded-lg font-semibold"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-white text-xl">No hay imágenes disponibles</div>
      </div>
    );
  }

  return (
    <ReelsToBuy
      items={items}
      maxFreeItems={3}
      
      // Precio según país
      price={`${product.recommended_currency} ${product.recommended_price}`}
      
      // Nombre del producto
      packName={product.name}
      
      // Personalización desde el dashboard
      buttonText={product.cta_button_text || 'Comprar Ahora'}
      buttonColor={product.cta_button_color || '#ec4899'}
      heroImage={product.hero_image_url}
      description={product.page_description || product.description}
      
      // Callback de compra
      onPurchaseClick={handlePurchase}
    />
  );
}
